// src/Components/Modules/HR/EmployeeList.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Chip,
  Button,
  Tooltip,
  Skeleton,
  Alert,
  Fab,
  InputAdornment,
  Stack,
  Avatar,
} from "@mui/material";
import {
  FaSearch, FaPlus, FaEye, FaEdit, FaToggleOn, FaToggleOff,
  FaFilter, FaDownload, FaSync, FaUserTie, FaPhone, FaEnvelope,
  FaBuilding, FaCalendarAlt, FaDollarSign, FaUsers, FaUserCheck,
  FaUserTimes, FaExclamationTriangle, FaBriefcase, FaMapMarkerAlt // ✅ FaMapMarkerAlt যোগ করা হয়েছে
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Custom styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const HeaderPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  borderRadius: theme.spacing(2),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  textAlign: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  minWidth: 140,
  flex: 1,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
  },
}));

const EmployeeList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const role = Number(localStorage.getItem("who_am_i"));
  const canManage = role <= 2;
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await AxiosAuthInstance.get("add_employee");
      setData(res.data.employee || []);
    } catch (err) {
      setError("Failed to load employees");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employees. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return data.filter(
      (e) =>
        e.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        e.employee_phone?.includes(search) ||
        e.employee_designation?.toLowerCase().includes(search.toLowerCase()) ||
        e.department?.toLowerCase().includes(search.toLowerCase()) ||
        e.employee_email?.toLowerCase().includes(search.toLowerCase()) // ✅ email এর পরিবর্তে employee_email
    );
  }, [data, search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatus = async (id, current) => {
    const result = await Swal.fire({
      title: current ? "Deactivate Employee?" : "Activate Employee?",
      text: `Are you sure you want to ${current ? "deactivate" : "activate"} this employee?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: current ? "#f44336" : "#4caf50",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, confirm",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.post(
          `update_employee_status/${id}/${current ? 0 : 1}`
        );
        setData((prev) =>
          prev.map((e) =>
            e.id === id ? { ...e, employee_status: !current } : e
          )
        );
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Status updated successfully",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update status",
        });
      }
    }
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={status ? "Active" : "Inactive"}
        size="small"
        sx={{
          backgroundColor: status ? "#e8f5e9" : "#ffebee",
          color: status ? "#2e7d32" : "#c62828",
          fontWeight: 600,
          fontSize: "0.75rem",
          "& .MuiChip-label": {
            padding: "0 10px",
          },
        }}
        icon={
          status ? (
            <FaUserCheck style={{ color: "#2e7d32", fontSize: 12 }} />
          ) : (
            <FaUserTimes style={{ color: "#c62828", fontSize: 12 }} />
          )
        }
      />
    );
  };

  const getInitials = (name) => {
    if (!name) return "EM";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const activeCount = data.filter((e) => e.employee_status).length;
  const inactiveCount = data.length - activeCount;

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Employee Management" />

      {/* Header Section */}
      <HeaderPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Employee Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage your team members and their information
            </Typography>
          </Box>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<FaPlus />}
              onClick={() => navigate("/hr/employees/add")}
              sx={{
                bgcolor: "white",
                color: "#667eea",
                "&:hover": { bgcolor: "#f0f0f0" },
                px: 3,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Add Employee
            </Button>
          )}
        </Box>

        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
          <StatCard elevation={0} sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white" }}>
            <FaUsers style={{ fontSize: 24, marginBottom: 8, opacity: 0.9 }} />
            <Typography variant="h3" fontWeight={700}>
              {data.length}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Total Employees
            </Typography>
          </StatCard>
          <StatCard elevation={0} sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white" }}>
            <FaUserCheck style={{ fontSize: 24, marginBottom: 8, opacity: 0.9 }} />
            <Typography variant="h3" fontWeight={700}>
              {activeCount}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Active
            </Typography>
          </StatCard>
          <StatCard elevation={0} sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white" }}>
            <FaUserTimes style={{ fontSize: 24, marginBottom: 8, opacity: 0.9 }} />
            <Typography variant="h3" fontWeight={700}>
              {inactiveCount}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Inactive
            </Typography>
          </StatCard>
        </Box>
      </HeaderPaper>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems="center">
          <TextField
            placeholder="Search by name, email, phone, designation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch color="#9e9e9e" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchEmployees} disabled={loading} sx={{ color: "#667eea" }}>
                <FaSync />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filter">
              <IconButton sx={{ color: "#667eea" }}>
                <FaFilter />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton sx={{ color: "#667eea" }}>
                <FaDownload />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={70}>SL</StyledTableCell>
              <StyledTableCell>Employee</StyledTableCell>
              <StyledTableCell>Contact</StyledTableCell>
              <StyledTableCell>Designation</StyledTableCell>
              <StyledTableCell>Department</StyledTableCell>
              <StyledTableCell>Workstation</StyledTableCell> {/* ✅ Workstation কলাম যোগ করা হয়েছে */}
              <StyledTableCell>Status</StyledTableCell>
              {canManage && <StyledTableCell align="center">Actions</StyledTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Skeleton Loading
              Array.from(new Array(5)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width={30} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box>
                        <Skeleton width={120} />
                        <Skeleton width={80} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Skeleton width={100} />
                    <Skeleton width={120} />
                  </TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={90} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell> {/* ✅ Workstation Skeleton */}
                  <TableCell><Skeleton width={80} /></TableCell>
                  {canManage && <TableCell><Skeleton width={120} /></TableCell>}
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 8 : 7} align="center" sx={{ py: 8 }}> {/* ✅ colSpan আপডেট করা হয়েছে */}
                  <Box sx={{ textAlign: "center" }}>
                    <FaExclamationTriangle style={{ fontSize: 48, color: "#e0e0e0", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No employees found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search ? "Try adjusting your search terms" : "Add your first employee to get started"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <StyledTableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {page * rowsPerPage + index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {/* ✅ FIX 1: Photo লজিক যোগ করা হয়েছে */}
                      <Avatar
                        sx={{
                          bgcolor: row.photo_path ? "transparent" : getColorFromName(row.employee_name),
                          width: 40,
                          height: 40,
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          overflow: "hidden",
                        }}
                      >
                        {row.photo_path ? (
                          <img 
                            src={`${process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000"}/storage/${row.photo_path}`} 
                            alt={row.employee_name} 
                            style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                          />
                        ) : (
                          getInitials(row.employee_name)
                        )}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {row.employee_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {row.employee_id || `EMP${row.id}`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <FaPhone style={{ fontSize: 10, color: "#9e9e9e" }} />
                        <Typography variant="body2">{row.employee_phone || "-"}</Typography>
                      </Box>
                      {row.employee_email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaEnvelope style={{ fontSize: 10, color: "#9e9e9e" }} />
                          <Typography variant="caption" color="text.secondary">
                            {row.employee_email}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.employee_designation || "-"}
                      size="small"
                      icon={<FaBriefcase style={{ fontSize: 10 }} />}
                      sx={{
                        bgcolor: "#e3f2fd",
                        color: "#1976d2",
                        fontWeight: 500,
                        "& .MuiChip-icon": { color: "#1976d2" },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <FaBuilding style={{ fontSize: 12, color: "#9e9e9e" }} />
                      <Typography variant="body2">{row.department || "-"}</Typography>
                    </Box>
                  </TableCell>
                  {/* ✅ FIX 2: Workstation কলামের ডাটা যোগ করা হয়েছে */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <FaMapMarkerAlt style={{ fontSize: 12, color: "#9e9e9e" }} />
                      <Typography variant="body2">{row.workstation?.name || "-"}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(row.employee_status)}</TableCell>
                  {canManage && (
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/hr/employees/${row.id}`)}
                            sx={{
                              color: "#2196f3",
                              bgcolor: "#e3f2fd",
                              "&:hover": { bgcolor: "#bbdefb" },
                            }}
                          >
                            <FaEye size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/hr/employees/${row.id}?mode=edit`)}
                            sx={{
                              color: "#ff9800",
                              bgcolor: "#fff3e0",
                              "&:hover": { bgcolor: "#ffe0b2" },
                            }}
                          >
                            <FaEdit size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.employee_status ? "Deactivate" : "Activate"}>
                          <IconButton
                            size="small"
                            onClick={() => handleStatus(row.id, row.employee_status)}
                            sx={{
                              color: row.employee_status ? "#f44336" : "#4caf50",
                              bgcolor: row.employee_status ? "#ffebee" : "#e8f5e9",
                              "&:hover": {
                                bgcolor: row.employee_status ? "#ffcdd2" : "#c8e6c9",
                              },
                            }}
                          >
                            {row.employee_status ? <FaToggleOff size={18} /> : <FaToggleOn size={18} />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>

        {!loading && filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingRight: 2,
              },
            }}
          />
        )}
      </StyledTableContainer>

      {/* Floating Action Button for Mobile */}
      {canManage && (
        <Fab
          color="primary"
          onClick={() => navigate("/hr/employees/add")}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            display: { xs: "flex", sm: "none" },
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
          }}
        >
          <FaPlus color="white" />
        </Fab>
      )}
    </Box>
  );
};

export default EmployeeList;